import { anglesService } from '@/shared/api/services/angles.service';
import {
  AngleValidationStatus,
  isAngleValidationFinished,
} from '@/shared/constants/status';
import { createLogger } from '@/shared/lib/logger';
import type { AngleValidation } from '@/types/concept';

const logger = createLogger('AngleValidationPolling');

// Single source of truth for angle-validation polling cadence — unified with
// the mobile platforms. Consumed by useValidationPolling (multi-item upload
// modal) and pollAngleValidation (single-angle flows).
export const VALIDATION_POLL_INTERVAL_MS = 3000;
export const VALIDATION_POLL_INITIAL_DELAY_MS = 10_000;
export const VALIDATION_PROCESSING_TIMEOUT_MS = 600_000;

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Poll a single angle until server-side validation finishes. Fallback for
 * flows where the `angles/validation_completed` WS event may be missed —
 * callers should abort the signal when the WS path resolves first.
 *
 * Semantics mirror useValidationPolling: 10s initial delay, 3s interval,
 * 600s budget counted only while the angle is PROCESSING (queue time is
 * unbounded). Transient fetch errors are logged and retried.
 *
 * @returns the finished validation, or null when processing exceeded the
 *          timeout budget.
 * @throws DOMException AbortError when the signal aborts.
 */
export async function pollAngleValidation(
  angleId: string,
  signal: AbortSignal
): Promise<AngleValidation | null> {
  await sleep(VALIDATION_POLL_INITIAL_DELAY_MS, signal);

  let processingElapsedMs = 0;

  for (;;) {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const angle = await anglesService.getAngle(angleId);
      const validation = angle.attributes?.validation;

      if (validation && isAngleValidationFinished(validation.status)) {
        return validation;
      }

      if (validation?.status === AngleValidationStatus.PROCESSING) {
        processingElapsedMs += VALIDATION_POLL_INTERVAL_MS;
        if (processingElapsedMs >= VALIDATION_PROCESSING_TIMEOUT_MS) {
          return null;
        }
      }
      // PENDING / missing validation — still in queue, doesn't count toward
      // the processing budget.
    } catch (error) {
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      logger.warn(`Polling error for angle ${angleId}:`, error);
    }

    await sleep(VALIDATION_POLL_INTERVAL_MS, signal);
  }
}
